import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { Space } from 'components';
import { BullResultCard } from 'components/BullSearch';
import { actions, Store } from 'rdx';
import { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BullListItem } from 'types';
import utils from 'utils';

const getBullEvaluationNames = (bull: BullListItem) => {
  if ('proof' in bull) {
    return bull.proof.flatMap(entry => Object.keys(entry ?? {}));
  }

  const { filters } = bull;
  const filterEvaluations = filters?.evaluations;
  const filterProofNames = filters?.proofNames;

  return [
    ...(('evaluations' in bull && Array.isArray(bull.evaluations)) ? bull.evaluations : []),
    ...(('proofNames' in bull && Array.isArray(bull.proofNames)) ? bull.proofNames : []),
    ...(Array.isArray(filterEvaluations) ? filterEvaluations : []),
    ...(Array.isArray(filterProofNames) ? filterProofNames : [])
  ];
};

const BullList: FC = () => {

  const { scope, items } = useSelector((store: Store) => store);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSingleSelection = (v: boolean, bull: BullListItem | number) => {
    const index = typeof bull === 'number'
      ? bull
      : items.bulls.findIndex(b => b.CRVcode === bull.CRVcode);
    dispatch(actions.items[v ? 'selectBull' : 'unselectBull'](index));
    if (!v) {
      dispatch(actions.items.removeAllSelected({
        type: 'breed',
        key: scope.breed || ''
      }));
    }
  };

  const onSelectAll = useCallback((v: boolean) => {
    items.scopedBulls.forEach(index => handleSingleSelection(v, index));
    dispatch(actions.items[v ? 'pushAllSelected' : 'removeAllSelected']({
      type: 'breed',
      key: scope.breed || ''
    }));
  }, [ handleSingleSelection ]);

  const currentBreed = items.breeds.find(b => b.name.toLowerCase() === scope.breed?.toLowerCase());
  const currentBreedBulls = currentBreed
    ? items.bulls.filter(b => b.breed?.toLowerCase?.() === currentBreed.name.toLowerCase())
    : [];
  const currentBreedEvaluationOptions = Array.from(new Set(
    currentBreedBulls.flatMap(getBullEvaluationNames)
  )).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(name => ({ id: name, name }));
  const ratingOptions = currentBreed
    ? currentBreedEvaluationOptions
    : [];

  let stamps: any[] = [];
  let stampsText: any[] = [];

  for (let i = 0; i < items.bulls.length; i++) {
    const bull = items.bulls[i];
    if (bull.breed == currentBreed?.name) {
      const bullStamps = bull.stamps ?? (bull as any).filters?.stamps ?? [];
      for (let j = 0; j < bullStamps.length; j++) {
        if (bullStamps[j] != `${bull.CRVcode}`) {
          if (stamps.indexOf(bullStamps[j].toUpperCase()) == -1) {
            const currentStamp = bullStamps[j].toUpperCase();
            if (currentStamp.indexOf('BANDEIRA') == -1) {
              stamps.push(currentStamp);
              stampsText.push(currentStamp.replace(/-/g, ' ').replace('ESQUERDA', ''));
            }
          }
        }
      }
    }
  }

  stamps = stamps.sort((a, b) => a.localeCompare(b));
  stampsText = stampsText.sort((a, b) => a.localeCompare(b));

  stamps = stamps.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  const nationalities: string[] = [ 'Nacional', 'Importado' ];

  const isCorteSegment = scope.segment === 'zc' || scope.segment === 'ec';

  const indexOptions = currentBreed && isCorteSegment
    ? Array.from(new Set(
      currentBreedBulls
        .flatMap(b => utils.bullIndices.getBullIndices(b))
        .map(idx => idx?.trim())
        .filter(Boolean) as string[]
    )).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    : [];
  const normalizedIndexSelection = isCorteSegment ? scope.indexFilter ?? 'all' : 'all';
  

  return (
    <Stack
      { ...utils.stack.flex.col('flex-start', 'center') }
      sx={ utils.sx.fw }
      spacing={ 2 }
    >
      <Stack direction='row' spacing={ 2 } width='100%'>
        {
          currentBreed &&
          <FormControl
            style={{ width: '40%' }}
            sx={{ maxWidth: '570px' }}
          >
            <InputLabel id='rating-label'> Avaliação </InputLabel>
            <Select
              labelId='rating-label'
              defaultValue='all'
              value={ scope.rating ?? 'all' }
              label='Avaliação'
              onChange={(e) => dispatch(actions.scope.changeRating(e.target.value))}
            >
              {
                ratingOptions.length === 0 &&
                <MenuItem disabled value=''> Nenhum avaliação encontrada </MenuItem>
              }
              {
                ratingOptions.length > 0 &&
                <MenuItem value='all'> Todas as avaliações </MenuItem>
              }
              {
                ratingOptions.map(p => (
                  <MenuItem key={p.name} value={p.name}>{ p.name }</MenuItem>
                ))
              }
            </Select>
          </FormControl>
        }
        {
          currentBreed &&
          <FormControl
            style={{ width: '40%' }}
            sx={{ maxWidth: '570px' }}
          >
            <InputLabel id='stamps-label'> Selos </InputLabel>
            <Select
              labelId='stamps-label'
              label='Selo'
              value={scope.stamps}
              renderValue={(selected) => selected.join(', ')}
              onChange={(e) => dispatch(actions.scope.changeStamps(e.target.value as string))}
              multiple={true}
            >
              {
                stamps.length === 0 &&
                <MenuItem disabled value=''> Nenhum selo encontrado </MenuItem>
              }
              {
                stamps.map((s, i) => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={scope.stamps.includes(s)} />
                    <ListItemText primary={stampsText[i]} />
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        }
        {
          (currentBreed && (currentBreed.name.toLowerCase() == 'aberdeen angus' || currentBreed.name.toLowerCase() == 'red angus')) &&
          <FormControl
            style={{ width: '20%' }}>
            <InputLabel id='nationality-label'> Origem </InputLabel>
            <Select
              labelId='nationality-label'
              value={scope.nationality ?? 'all'}
              defaultValue='all'
              label='Origem'
              onChange={(e) => dispatch(actions.scope.changeNationality(e.target.value))}
              sx={{ maxWidth: '570px' }}

            >
              {
                nationalities.length > 0 &&
                <MenuItem value='all'> Todas </MenuItem>
              }
              {
                nationalities.map(n => (
                  <MenuItem key={ n } value={ n }>{ n }</MenuItem>
                ))
              }
            </Select>
          </FormControl>
        }
        { 
          currentBreed &&
          <FormControl style={{ width: '20%' }}>
            <InputLabel id='newrelease-label'> Lançamento </InputLabel>
            <Select
              labelId='newrelease-label'
              value={scope.newRelease ?? '-'}
              defaultValue='-'
              label='Lancamento'
              onChange={(e) => dispatch(actions.scope.changeNewRelease(e.target.value))}
              sx={{ maxWidth: '570px' }}
            >
              {
                <MenuItem value='-'> Todas </MenuItem>
              }
              {
                <MenuItem value='x'> Lançamento </MenuItem>
              }
            </Select>
          </FormControl>
        }
        {
          currentBreed &&
          <FormControl style={{ width: '20%' }}>
            <InputLabel id='index-filter-label'> Índices </InputLabel>
            <Select
              labelId='index-filter-label'
              value={normalizedIndexSelection}
              defaultValue='all'
              label='Índices'
              onChange={(e) => dispatch(actions.scope.changeIndexFilter(e.target.value))}
              sx={{ maxWidth: '570px' }}
            >
              {
                indexOptions.length === 0 &&
                <MenuItem disabled value='all'> Nenhum índice encontrado </MenuItem>
              }
              {
                indexOptions.length > 0 &&
                <MenuItem value='all'> Todos </MenuItem>
              }
              {
                indexOptions.map(index => (
                  <MenuItem key={index} value={index}>{ index }</MenuItem>
                ))
              }
            </Select>
          </FormControl>
        }
      </Stack>
      <Stack
        { ...utils.stack.flex.row('flex-start', 'center') }
        sx={ utils.sx.fw }
        spacing={ 1 }
      >
        <Checkbox
          checked={ utils.isAllSelected(items.allSelected, scope, 'breed') }
          sx={(theme) => ({ color: theme.palette.primary.main, padding: '0px' })}
          onChange={(_, v) => onSelectAll(v)}
        />
        <Typography> Selecionar todos ({ items.scopedBulls.length }) </Typography>
      </Stack>
      <Space size={ 1 } key='breed-page-space-1' />
      <Stack
        { ...utils.stack.flex.col('flex-start', 'flex-start') }
        sx={{ width: '100%' }}
        spacing={ 2 }
      >
        {
          items.scopedBulls.map((n) => {
            const bull = items.bulls[n];
            return (
              <>
                {/* <p>{n}</p> */}
                <BullResultCard
                  bull={ bull }
                  key={ bull.name }
                  onClick={() => navigate(utils.string.printableToPathlike(bull.CRVcode.toUpperCase()))}
                  onChange={(v) => handleSingleSelection(v, bull)}
                  showIndices={ isCorteSegment }
                />
              </>
            );
          })
        }
      </Stack>
    </Stack>
  );
};

export default BullList;
